'use client'

import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import eventsData from '../events.json'

export default function EventDetailPage() {
  const { t, language } = useLanguage()
  const params = useParams()
  const eventId = params.eventId as string
  const lang = appLanguageToContentLocale(language)

  // 直接用 eventId 查找事件
  const event = (eventsData as any)[eventId]

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bauhaus text-white mb-4">{t('bpf.events.details.not_found') || 'Not Found'}</h1>
          <Link to="/Qiaodong/BPF/events" className="text-[#B9AE9A] hover:text-white transition-colors">
            {t('bpf.events.details.back_to_list') || '返回活动列表'}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-20 pb-8 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-[200px] bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/Qiaodong/BPF/events"
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.events.details.back_to_list') || t('bpf.events.title')}
            </Link>
          </motion.div>

          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {event[lang]?.title || event.zh?.title}
              </span>
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex items-center justify-center space-x-4"
            >
              <div className="bg-[#B9AE9A]/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-[#B9AE9A] font-bauhaus text-sm">
                  {event.date}
                </span>
              </div>
              <div className="bg-[#8C4233]/20 backdrop-blur-sm rounded-lg px-4 py-2">
                <span className="text-[#8C4233] font-bauhaus text-sm">
                  {event[lang]?.time || event.zh?.time}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* 活动简介 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-16"
        >
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl overflow-hidden">
            <div className="p-8 md:p-12">
              <h2 className="text-2xl font-bauhaus text-white mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                  {t('bpf.events.details.intro') || '简介'}
                </span>
              </h2>
              <div className="space-y-4 text-white/80 text-lg leading-relaxed">
                <p className="whitespace-pre-line">{event[lang]?.description || event.zh?.description}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 嘉宾卡片 */}
        {eventId === 'nach-den-schlusspunkt' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bauhaus text-white mb-8 text-center">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {t('bpf.events.details.guests') || '嘉宾'}
              </span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Sool Park', title: '诗人、翻译家', link: 'Sool_Park_Talk' },
                { name: 'Li Jing', title: '诗人、翻译家', link: 'Li_Jing_Talk' },
                { name: 'Juntao Ye', title: '诗人、翻译家', link: 'Juntao_Ye_Talk' },
                { name: 'Yufeng Jiang', title: '诗人、翻译家', link: 'Yufeng_Jiang_Talk' },
                { name: 'Xiaowan Jin', title: '诗人、翻译家', link: 'Xiaowan_Jin_Talk' },
                { name: 'Yu-Sheng Tsou', title: '诗人、翻译家', link: 'Yu-Sheng_Tsou_talk' }
              ].map((guest, index) => (
                <Link 
                  key={guest.name}
                  to={`/Qiaodong/BPF/events/${eventId}/${guest.link}`}
                  className="group"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl p-6 hover:border-[#B9AE9A]/30 transition-all duration-300"
                  >
                    <h3 className="text-xl font-bauhaus text-white mb-2 group-hover:text-[#B9AE9A] transition-colors">
                      {guest.name}
                    </h3>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* 活动信息 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {/* 时间地点 */}
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl p-8">
            <h3 className="text-xl font-bauhaus text-white mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {t('bpf.events.details.time_location') || '时间 & 地点'}
              </span>
            </h3>
            <div className="space-y-3 text-white/70">
              <div className="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-6 0h6m-6 0V7a1 1 0 011-1h4a1 1 0 011 1v4M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{event.date}</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{event[lang]?.location || event.zh?.location}</span>
              </div>
            </div>
          </div>

          {/* 参与信息 */}
          <div className="bg-[#1C1C1C]/30 backdrop-blur-sm border border-[#B9AE9A]/10 rounded-xl p-8">
            <h3 className="text-xl font-bauhaus text-white mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {t('bpf.events.details.guests_keywords') || '嘉宾 & 关键词'}
              </span>
            </h3>
            <div className="space-y-3 text-white/70">
              <div className="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <div className="flex flex-col">
                  <span>{event[lang]?.guests || event.zh?.guests}</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B9AE9A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{event[lang]?.keywords || event.zh?.keywords}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 